import {
    Edit,
    Check,
    Delete,
    Add,
    ContentCopy,
} from "@mui/icons-material";
import {
    Grid,
    Typography,
    IconButton,
    Tooltip,
    FormControlLabel,
    Checkbox,
    Stack,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import ConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import { LLMExecutorStep, LLMModel } from "models/LLMs";
import React, {
    useState,
    useCallback,
    createContext,
    useContext,
    useEffect,
} from "react";
import { AllLLMPromptTemplateSelector } from "../LLMPromptTemplateSelector";
import { useLogicChainConfigurationBuilder } from "../LLMConfigurationBuilderContext";
import { StepAction } from "./StepAction";
import { StepPrompt } from "./StepPrompt";
import JsonSchemaEditorControlled from "components/JsonSchema/JsonSchemaEditorControlled";
import { DEFAULT_SCHEMA } from "components/JsonSchema/constants";
import { LLMModelGenerationSettings } from "components/LLMs/LLMModelGenerationSettings";
import LLMModelsService from "services/LLMModelService";

type LogicChainStepProps = {
    step: LLMExecutorStep;
    defaultModel?: LLMModel;
};

const StepContext = createContext<LLMExecutorStep | null>(null);
export function useCurrentStep() {
    const step = useContext(StepContext);
    if (!step) throw new Error("Invalid hook usage. No step.");
    return step;
}

export const Step = observer(function (props: LogicChainStepProps) {
    const builder = useLogicChainConfigurationBuilder();

    const [isEditingName, setIsEditingName] = useState(false);
    const [editingName, setEditingName] = useState(props.step.name);
    const [llmModels, setLlmModels] = useState<LLMModel[]>([]);

    const [potentialTemplate, setPotentialTemplate] = useState<string>();
    const [isConfirmPromptOverrideOpen, setIsConfirmPromptOverrideOpen] =
        useState(false);

    useEffect(() => {
        let isMounted = true;
        const service = new LLMModelsService();

        service.getLLMModels().then((models) => {
            if (isMounted) setLlmModels(models);
        });

        return () => {
            isMounted = false;
        };
    }, []);

    const handlePromptOverride = useCallback(
        (template: string) => {
            if (
                !props.step.configuration.prompt ||
                props.step.configuration.prompt === ""
            )
                return false;

            setPotentialTemplate(template);
            setIsConfirmPromptOverrideOpen(true);
            return true;
        },
        [props.step.configuration.prompt],
    );

    const handleEditName = useCallback(() => {
        if (!editingName || editingName.length === 0) return;

        const oldStepId = props.step.id;

        props.step.setName(editingName);
        for (const step of builder.steps) {
            step.updatePromptStepId(oldStepId, props.step.id);
        }

        setIsEditingName(false);
    }, [props.step, editingName, builder.steps]);

    return (
        <StepContext.Provider value={props.step}>
            <Grid
                container
                item
                direction="column"
                flexWrap="nowrap"
                rowGap={1}
            >
                <Grid
                    container
                    item
                    columnGap={1}
                    flexWrap="nowrap"
                    justifyContent="space-between"
                >
                    <Grid
                        container
                        item
                        columnGap={1}
                        width="fit-content"
                        alignItems="center"
                    >
                        {!isEditingName && (
                            <React.Fragment>
                                <Grid item>
                                    <Typography
                                        component="span"
                                        sx={(theme) => ({
                                            color: theme.palette.gray[500],
                                            fontWeight: 600,
                                            fontSize: "14px",
                                        })}
                                    >
                                        {props.step.name}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <IconButton
                                        size="small"
                                        sx={() => ({ fontSize: "13px" })}
                                        onClick={() => setIsEditingName(true)}
                                    >
                                        <Edit fontSize="inherit" />
                                    </IconButton>
                                </Grid>
                                <Grid item>
                                    <Typography
                                        component="span"
                                        sx={(theme) => ({
                                            color: theme.palette.gray[400],
                                            fontSize: "12px",
                                        })}
                                    >
                                        ID: {props.step.id}
                                    </Typography>
                                    <Tooltip title="Copy step ID">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                navigator.clipboard.writeText(
                                                    props.step.id,
                                                );
                                            }}
                                        >
                                            <ContentCopy
                                                fontSize="small"
                                                sx={{ fontSize: "14px" }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </React.Fragment>
                        )}
                        {isEditingName && (
                            <React.Fragment>
                                <Grid item>
                                    <AcxMainTextField
                                        id={`${props.step.reactKey}-name`}
                                        autofocus
                                        blurOnEnterPress
                                        value={editingName}
                                        onChange={(event) => {
                                            setEditingName(event.target.value);
                                        }}
                                        onBlur={() => handleEditName()}
                                        endAdornment={
                                            <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                    handleEditName();
                                                }}
                                            >
                                                <Check fontSize="small" />
                                            </IconButton>
                                        }
                                    />
                                </Grid>
                            </React.Fragment>
                        )}
                    </Grid>
                    <Grid
                        container
                        item
                        columnGap={2}
                        alignItems="center"
                        width="fit-content"
                    >
                        <Grid item>
                            <Tooltip title="Remove step">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        builder.removeStep(props.step)
                                    }
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={props.step.isEntryPoint}
                                        onChange={(event) =>
                                            props.step.setIsEntryPoint(
                                                event.target.checked,
                                            )
                                        }
                                    />
                                }
                                label="Is entry point"
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    container
                    item
                    columns={2}
                    columnGap={2}
                    flexWrap="nowrap"
                >
                    <Grid item xs={1}>
                        <LLMModelGenerationSettings
                            id={`${props.step.reactKey}-model-generation`}
                            availableModels={llmModels}
                            value={
                                props.step.llmModelName ||
                                props.step.modelOptions
                                    ? {
                                          modelName:
                                              props.step.llmModelName ?? null,
                                          temperature:
                                              props.step.modelOptions
                                                  ?.temperature ?? null,
                                          thinkingEffortLevel:
                                              props.step.modelOptions
                                                  ?.thinkingEffortLevel ?? null,
                                      }
                                    : null
                            }
                            defaultValue={
                                props.defaultModel
                                    ? { modelName: props.defaultModel.name }
                                    : null
                            }
                            modelLabel="LLM Model"
                            modelPlaceholder="Use default model"
                            onChange={(value) =>
                                props.step.setModelGenerationSettings(value)
                            }
                            onModelReset={() =>
                                props.step.setModelGenerationSettings(null)
                            }
                            onGenerationReset={() =>
                                props.step.setModelGenerationSettings(
                                    props.step.llmModelName
                                        ? {
                                              modelName:
                                                  props.step.llmModelName,
                                          }
                                        : null,
                                )
                            }
                        />
                    </Grid>
                    <Grid item xs={1}>
                        <AllLLMPromptTemplateSelector
                            inputLabel="LLM Prompt Template"
                            placeholder="Select a template to fill the Prompt input"
                            onChange={(template) => {
                                const value = template.template;
                                if (handlePromptOverride(value)) return;
                                props.step.configuration.setPrompt(value);
                            }}
                        />
                        <ConfirmationDialog
                            title="Override current prompt?"
                            content="Clicking confirm will cause your current prompt to be overwriten by the selected template"
                            isOpen={isConfirmPromptOverrideOpen}
                            onClose={() =>
                                setIsConfirmPromptOverrideOpen(false)
                            }
                            onCancel={() =>
                                setIsConfirmPromptOverrideOpen(false)
                            }
                            onConfirm={() => {
                                if (!potentialTemplate)
                                    return setIsConfirmPromptOverrideOpen(
                                        false,
                                    );
                                props.step.configuration.setPrompt(
                                    potentialTemplate,
                                );
                                setIsConfirmPromptOverrideOpen(false);
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid item sx={(theme) => ({ marginBottom: theme.spacing(1) })}>
                    <StepPrompt />
                </Grid>
                <Grid item>
                    <Stack direction="row" spacing={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={
                                        props.step.configuration
                                            .useStructuredOutputs
                                    }
                                    onChange={(e) => {
                                        props.step.configuration.setUseStructuredOutputs(
                                            e.target.checked,
                                        );
                                    }}
                                />
                            }
                            label="Use Structured Outputs"
                        />
                    </Stack>
                </Grid>
                {props.step.configuration.useStructuredOutputs && (
                    <Grid item>
                        <JsonSchemaEditorControlled
                            value={
                                props.step.configuration
                                    .structuredOutputJsonSchema ??
                                DEFAULT_SCHEMA
                            }
                            onChange={(schema) =>
                                props.step.configuration.setStructuredOutputJsonSchema(
                                    schema,
                                )
                            }
                        />
                    </Grid>
                )}
                <Grid
                    container
                    item
                    direction="column"
                    flexWrap="nowrap"
                    rowGap={2}
                >
                    {props.step.configuration.actions.map((action, idx) => (
                        <StepAction
                            key={`step-${props.step.reactKey}-action-${idx}`}
                            action={action}
                        />
                    ))}
                </Grid>
                <Grid item>
                    <AcxButton
                        color="white"
                        sx={(theme) => ({
                            color: theme.palette.primary.main,
                        })}
                        startIcon={<Add />}
                        onClick={() => props.step.configuration.addAction()}
                    >
                        If
                    </AcxButton>
                </Grid>
            </Grid>
        </StepContext.Provider>
    );
});
